import {
  BookingMoveBatchMode,
  ReviewDecision,
  type BookingReviewDetail,
  type ReviewDecisionValue,
} from "@/app/booking-calendar/api/reviewApi"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import { appointmentWindowLabel } from "./reviewBookingSummary"

const ALL_DECISIONS: readonly ReviewDecisionValue[] = [ReviewDecision.Keep, ReviewDecision.CancelDuplicate]

/**
 * The decision buttons the review dialog offers, in order (AB#809): what the server says the request
 * accepts — Keep alone for a failed batch move — and both when it does not say (an older server).
 * Anything the client cannot name is dropped rather than offered as a button that would be refused.
 */
export function allowedDecisions(detail: BookingReviewDetail | null | undefined): ReviewDecisionValue[] {
  const allowed = detail?.allowedDecisions
  if (!allowed) return [...ALL_DECISIONS]
  return ALL_DECISIONS.filter((decision) => allowed.includes(decision))
}

/** The "Batch / attempted target / failure" lines of a failed batch move, or null when there is none. */
export interface RescheduleFailureLines {
  batch: string
  target: string
  failure: string
}

/** The failed batch move's block, in the site's zone; null for a request without one. */
export function rescheduleFailureLines(
  detail: BookingReviewDetail | null | undefined,
  timeZone: string = SITE_TIME_ZONE,
): RescheduleFailureLines | null {
  const failure = detail?.rescheduleFailure
  if (!failure) return null

  const mode = failure.mode === BookingMoveBatchMode.Automatic ? "Automatic reassignment" : "Manual reschedule"
  const host = failure.targetHostName || failure.targetHostEmail || `Cal user ${failure.targetCalUserId}`
  const when = appointmentWindowLabel(failure.targetStart, null, timeZone) ?? failure.targetStart

  return {
    batch: `${mode} · batch ${failure.batchId.slice(0, 8)}`,
    target: `${host} · ${when}`,
    failure: failure.failureDetail || "No reason was recorded.",
  }
}
